const ATTRIBUTION_FIELDS = [
  "gclid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "sb_landing_url",
  "sb_referrer_url",
];
const ATTRIBUTION_STORAGE_KEY = "sb_signup_attribution";
const ATTRIBUTION_EXPIRY_FIELD = "_expires_at";
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const SIGNUP_HOST = "app.salesbinder.com";
const SIGNUP_PATH_PREFIX = "/signup/";
const MAX_URL_LENGTH = 2000;

const isBrowser =
  typeof window !== "undefined" && typeof document !== "undefined";

const hasOwn = (value, field) =>
  Object.prototype.hasOwnProperty.call(value, field);

export const isSalesBinderHostname = (hostname) => {
  const normalizedHostname = String(hostname || "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");

  return (
    normalizedHostname === "salesbinder.com" ||
    normalizedHostname.endsWith(".salesbinder.com")
  );
};

export const normalizeAttributionUrl = (value) => {
  if (!value) {
    return "";
  }

  try {
    const url = new URL(String(value).trim());
    if (
      !["http:", "https:"].includes(url.protocol) ||
      url.username ||
      url.password
    ) {
      return "";
    }

    url.hash = "";

    return url.toString().slice(0, MAX_URL_LENGTH);
  } catch (error) {
    return "";
  }
};

export const captureFirstTouchAttribution = ({
  existing = {},
  locationHref = "",
  locationSearch = "",
  referrerUrl = "",
  now = Date.now(),
}) => {
  const attribution =
    existing && typeof existing === "object" ? { ...existing } : {};
  const query = new URLSearchParams(locationSearch || "");

  ATTRIBUTION_FIELDS.filter(
    (field) => !["sb_landing_url", "sb_referrer_url"].includes(field),
  ).forEach((field) => {
    const value = (query.get(field) || "").trim();
    if (value && !attribution[field]) {
      attribution[field] = value.slice(0, 255);
    }
  });

  if (!hasOwn(attribution, "sb_landing_url")) {
    attribution.sb_landing_url = normalizeAttributionUrl(locationHref);
  }

  if (!hasOwn(attribution, "sb_referrer_url")) {
    const normalizedReferrer = normalizeAttributionUrl(referrerUrl);
    attribution.sb_referrer_url =
      normalizedReferrer &&
      !isSalesBinderHostname(new URL(normalizedReferrer).hostname)
        ? normalizedReferrer
        : "";
  }

  if (!Number(attribution[ATTRIBUTION_EXPIRY_FIELD])) {
    attribution[ATTRIBUTION_EXPIRY_FIELD] = now + ATTRIBUTION_TTL_MS;
  }

  return attribution;
};

const readStorageValue = (storage) => {
  if (!storage) {
    return {};
  }

  const value = JSON.parse(storage.getItem(ATTRIBUTION_STORAGE_KEY) || "{}");
  if (!value || typeof value !== "object") {
    return {};
  }

  const expiresAt = Number(value[ATTRIBUTION_EXPIRY_FIELD] || 0);
  if (expiresAt && expiresAt <= Date.now()) {
    storage.removeItem(ATTRIBUTION_STORAGE_KEY);

    return {};
  }

  return value;
};

const readStoredAttribution = () => {
  if (!isBrowser) {
    return {};
  }

  try {
    const value = readStorageValue(window.localStorage);
    if (Object.keys(value).length > 0) {
      return value;
    }
  } catch (error) {
    // Fall back to the previous session-only storage behavior.
  }

  try {
    return readStorageValue(window.sessionStorage);
  } catch (error) {
    return {};
  }
};

const captureAttribution = (location) => {
  if (!isBrowser || !location) {
    return readStoredAttribution();
  }

  const attribution = captureFirstTouchAttribution({
    existing: readStoredAttribution(),
    locationHref: location.href,
    locationSearch: location.search,
    referrerUrl: document.referrer,
  });

  try {
    window.localStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(attribution),
    );
  } catch (error) {
    try {
      window.sessionStorage.setItem(
        ATTRIBUTION_STORAGE_KEY,
        JSON.stringify(attribution),
      );
    } catch (storageError) {
      // Signup links still work when storage is unavailable.
    }
  }

  return attribution;
};

export const buildAttributedSignupUrl = (
  href,
  attribution,
  currentHref = "https://www.salesbinder.com/",
) => {
  const url = new URL(href, currentHref);
  if (
    url.hostname !== SIGNUP_HOST ||
    !url.pathname.startsWith(SIGNUP_PATH_PREFIX)
  ) {
    return url.toString();
  }

  ATTRIBUTION_FIELDS.forEach((field) => {
    if (attribution[field] && !url.searchParams.has(field)) {
      url.searchParams.set(field, attribution[field]);
    }
  });

  return url.toString();
};

const decorateSignupLink = (link, attribution) => {
  if (!link || !link.href) {
    return;
  }

  link.href = buildAttributedSignupUrl(
    link.href,
    attribution,
    window.location.href,
  );
};

export const syncSignupAttribution = (location = window.location) => {
  if (!isBrowser) {
    return;
  }

  const attribution = captureAttribution(location);
  document.querySelectorAll("a[href]").forEach((link) => {
    decorateSignupLink(link, attribution);
  });
};

export const initializeSignupAttribution = () => {
  if (!isBrowser) {
    return;
  }

  syncSignupAttribution(window.location);
  document.addEventListener(
    "click",
    (event) => {
      const link = event.target.closest
        ? event.target.closest("a[href]")
        : null;
      decorateSignupLink(link, readStoredAttribution());
    },
    true,
  );
};
